import { useEffect } from "react"
import { useCartStore } from "@/store/cart/cart-store"
import { selectCartItems } from "@/store/cart/cart.selectors"
import { useAppDispatch } from "@/store/store"
import { setBillingPreview } from "@/slices/final-billing-slice"
import {
  usePricingPreview,
  type UsePricingPreviewParams,
  type UsePricingPreviewResult,
} from "@/hooks/usePricingPreview"

export type UseBillingPreviewSyncParams = UsePricingPreviewParams
export type BillingPreviewSyncResult = UsePricingPreviewResult

/**
 * Keeps Redux `finalBilling.billingPreview` synchronized with the selected discount and cart.
 *
 * Single responsibility: discount/cart identity changes → authoritative `previewOrder` →
 * billing preview state. No offer ranking, auto-selection, or UI.
 *
 * Reuses {@link usePricingPreview} (safe-query cache, debounce, `setBillingPreview`,
 * Pricing Confirmation ownership).
 */
export function useBillingPreviewSync(
  params: UseBillingPreviewSyncParams
): BillingPreviewSyncResult {
  const dispatch = useAppDispatch()
  const items = useCartStore(selectCartItems)
  const result = usePricingPreview(params)

  useEffect(() => {
    if (items.length === 0) {
      dispatch(setBillingPreview(null))
    }
  }, [items.length, dispatch])

  return result
}
